/**
 * Pure helpers for the General Email Templates send history
 * (pages/admin/TemplateSendHistory.jsx) and its Logs drill-down. No React, no
 * network — tested in sendHistory.test.js. Dates are IST calendar days
 * (YYYY-MM-DD), the unit the backend filters by
 * (backend/services/template_send_history_service.py).
 */

export const LOG_PAGE_SIZE = 10;

const IST_DAY = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" });

/** The IST calendar day of `date` as YYYY-MM-DD. */
export const istDay = (date) => IST_DAY.format(date);

const DAY_MS = 24 * 60 * 60 * 1000;

export const PRESETS = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "month", label: "This month" },
];

/** { from, to } (IST days, inclusive) for a preset; nulls = no limit. */
export function presetRange(preset, now = new Date()) {
  const today = istDay(now);
  switch (preset) {
    case "today": return { from: today, to: today };
    case "7d": return { from: istDay(new Date(now.getTime() - 6 * DAY_MS)), to: today };
    case "30d": return { from: istDay(new Date(now.getTime() - 29 * DAY_MS)), to: today };
    case "month": return { from: `${today.slice(0, 8)}01`, to: today };
    default: return { from: null, to: null };
  }
}

/** Why a custom range cannot be used (null = it can). */
export function rangeError({ from, to }) {
  if (from && to && from > to) return "The From date is after the To date.";
  return null;
}

/** Query params for the backend; empty ends are left out. */
export const rangeParams = ({ from, to }) => ({
  ...(from ? { date_from: from } : {}),
  ...(to ? { date_to: to } : {}),
});

const DAY_LABEL = new Intl.DateTimeFormat("en-IN", { timeZone: "UTC", day: "numeric", month: "short", year: "numeric" });

/** "24 Sept 2026 – 24 Oct 2026", "Since 1 Oct 2026", "All time". */
export function rangeLabel({ from, to }) {
  const d = (s) => DAY_LABEL.format(new Date(`${s}T00:00:00Z`));
  if (from && to) return from === to ? d(from) : `${d(from)} – ${d(to)}`;
  if (from) return `Since ${d(from)}`;
  if (to) return `Until ${d(to)}`;
  return "All time";
}

const WHEN = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata", day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit",
});

/** A stored UTC ISO time shown in IST; "—" when missing. */
export const formatWhen = (iso) => (iso ? WHEN.format(new Date(iso)) : "—");

/**
 * GET …/template-send-stats → flat table rows: one per template, and under Common
 * Client one per announcement type (level 1).
 */
export function historyRows(stats) {
  const rows = [];
  (stats?.templates || []).forEach((t) => {
    rows.push({ key: t.notification_type, level: 0, label: t.label, notification_type: t.notification_type,
      announcement_type: null, sends: t.sends, failed: t.failed, emails_delivered: t.emails_delivered,
      last_sent_at: t.last_sent_at });
    (t.types || []).forEach((a) => rows.push({
      key: `${t.notification_type}:${a.announcement_type}`, level: 1, label: a.label, parentLabel: t.label,
      notification_type: t.notification_type, announcement_type: a.announcement_type, sends: a.sends,
      failed: a.failed, emails_delivered: a.emails_delivered, last_sent_at: a.last_sent_at,
    }));
  });
  return rows;
}

/** GET …/admin/logs params for one page of a (possibly filtered) Logs view. */
export const logsParams = (page, filter) => ({
  limit: LOG_PAGE_SIZE,
  skip: (page - 1) * LOG_PAGE_SIZE,
  ...(filter?.notification_type ? { notification_type: filter.notification_type } : {}),
  ...(filter?.announcement_type ? { announcement_type: filter.announcement_type } : {}),
  ...(filter ? rangeParams(filter) : {}),
});
